import { Outlet, useLocation } from 'react-router-dom'
import { AppSidebar } from '@/components/app-sidebar'
import { 
  PageHeader, 
  PageHeaderContent, 
  PageHeaderLeft, 
  PageHeaderTitle, 
  PageHeaderActions,
  PageMain
} from '@/components/page-header'
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar'
import { getPageConfig } from '@/lib/page-config'
import { WeekProvider } from '@/context/week-context'
import { InsightsWeekProvider, useInsightsWeek } from '@/context/insights-week-context'
import { PlanningWeekProvider, usePlanningWeek } from '@/context/planning-week-context'
import { ChatProvider, useChat } from '@/context/chat-context'
import { WeekNavigation } from '@/components/week-navigation'
import { Button } from '@/components/ui/button'
import { Trash2 } from '@/components/icons'

// Component that uses appropriate WeekContext based on current page
function AppHeaderContent() {
  const location = useLocation()
  const pageConfig = getPageConfig(location.pathname)
  const { currentWeekStart: insightsWeekStart, setCurrentWeekStart: setInsightsWeekStart } = useInsightsWeek()
  const { currentWeekStart: planningWeekStart, setCurrentWeekStart: setPlanningWeekStart } = usePlanningWeek()

  // Get dynamic actions based on current page
  const getPageActions = () => {
    if (location.pathname === '/insights') {
      return (
        <WeekNavigation 
          currentWeekStart={insightsWeekStart}
          onWeekChange={setInsightsWeekStart}
        />
      )
    }
    if (location.pathname === '/planning') {
      return (
        <WeekNavigation 
          currentWeekStart={planningWeekStart}
          onWeekChange={setPlanningWeekStart}
        />
      )
    }
    return pageConfig.actions
  }

  return (
    <PageHeader>
      <PageHeaderContent>
        <PageHeaderLeft>
          <PageHeaderTitle>{pageConfig.title}</PageHeaderTitle>
          {pageConfig.leftContent}
        </PageHeaderLeft>
        <PageHeaderActions>
          {getPageActions()}
        </PageHeaderActions>
      </PageHeaderContent>
    </PageHeader>
  )
}

// Component that uses ChatContext for koba page actions
function ChatHeaderContent() {
  const { conversationTitle, sessionId, isLoading, isStreaming, clearChat } = useChat()

  return (
    <PageHeader>
      <PageHeaderContent>
        <PageHeaderLeft>
          <div className="flex items-center gap-3">
            <PageHeaderTitle>{conversationTitle}</PageHeaderTitle>
            <span className="text-xs text-muted-foreground">
              Session: {sessionId.slice(0, 8)}...
            </span>
          </div>
        </PageHeaderLeft>
        <PageHeaderActions>
          <Button
            variant="ghost"
            size="sm"
            onClick={clearChat}
            disabled={isLoading || isStreaming}
            className="h-8 w-8 p-0"
            title="Clear chat"
          >
            <Trash2 className="h-5 w-5" />
          </Button>
        </PageHeaderActions>
      </PageHeaderContent>
    </PageHeader>
  )
}

export function AppLayout() {
  return (
    <WeekProvider>
      <InsightsWeekProvider>
        <PlanningWeekProvider>
          <ChatProvider>
            <SidebarProvider defaultOpen={false}>
              <AppSidebar />
              <SidebarInset>
                {/* Conditional Page Header */}
                <AppHeaderWithContext />
                
                {/* Page content */}
                <PageMain>
                  <Outlet />
                </PageMain>
              </SidebarInset>
            </SidebarProvider>
          </ChatProvider>
        </PlanningWeekProvider>
      </InsightsWeekProvider>
    </WeekProvider>
  )
}

// Component that conditionally renders the appropriate header
function AppHeaderWithContext() {
  const location = useLocation()
  
  // Use chat header for koba page, regular header for others
  if (location.pathname === '/koba') {
    return <ChatHeaderContent />
  }
  
  return <AppHeaderContent />
}
